// Frontend permission gates (Slice 15).
//
// Pure helpers — no React, no fetch. The backend remains the authoritative
// source of permission enforcement; these helpers exist purely as UX polish
// so users don't see buttons that would 403.
//
// Role taxonomy (mirrors backend `_FULL_VIEW_ROLES` / scrape allowlist):
//   - supervisore, analista, responsabile, lead_manager  → full view
//   - 'responsabile' present in secondary_roles           → full view
//   - consulente                                          → own scope only
//   - consulente_ex, lead_manager_ex                      → read-only
//
// All helpers accept `MeResponse | null | undefined` and default to the
// most-restrictive answer (false / read-only) when the user is unknown.

import type { MeResponse } from './api'

const FULL_VIEW_ROLES: ReadonlySet<string> = new Set([
  'supervisore',
  'analista',
  'responsabile',
  'lead_manager',
])

const ADMIN_SCRAPE_ROLES: ReadonlySet<string> = new Set([
  'supervisore',
  'analista',
  'responsabile',
])

const READ_ONLY_ROLES: ReadonlySet<string> = new Set([
  'consulente_ex',
  'lead_manager_ex',
])

type Me = MeResponse | null | undefined

function hasResponsabileSecondary(me: MeResponse): boolean {
  return (me.secondary_roles ?? []).includes('responsabile')
}

export function isFullView(me: Me): boolean {
  if (!me) return false
  if (FULL_VIEW_ROLES.has(me.role)) return true
  return hasResponsabileSecondary(me)
}

export function canTriggerScrape(me: Me): boolean {
  if (!me) return false
  if (ADMIN_SCRAPE_ROLES.has(me.role)) return true
  return hasResponsabileSecondary(me)
}

export function canWriteParticipation(me: Me): boolean {
  // Anyone except read-only ex roles. Backend enforces own-scope for consulenti.
  if (!me) return false
  return !READ_ONLY_ROLES.has(me.role)
}

export function canWriteClient(me: Me): boolean {
  // Same rule as participation in v1.
  return canWriteParticipation(me)
}

export function canEditOtherConsultant(me: Me): boolean {
  // Edit clients/properties not owned by self → only full-view roles.
  return isFullView(me)
}

export function canDeleteVault(me: Me, vault_owner_id: string): boolean {
  if (!me) return false
  if (vault_owner_id === me.id) return true
  return isFullView(me)
}

export function isReadOnly(me: Me): boolean {
  if (!me) return true
  return READ_ONLY_ROLES.has(me.role)
}

// KPI sidebar link: visibile a TUTTI (anche consulenti — vedono solo proprie).
// Backend filtra row per ruolo (consulente → user_id=self forced).
export function canViewKpi(_me: Me): boolean {
  return true
}

// Professionals write: tutti tranne viewer/assistente/analista/marketing
const PROF_READ_ONLY_ROLES: ReadonlySet<string> = new Set([
  'viewer',
  'assistente',
  'analista',
  'marketing',
])

export function canWriteProfessional(me: Me): boolean {
  if (!me) return false
  return !PROF_READ_ONLY_ROLES.has(me.role)
}
